<template>
  <div class="rewards-page">
    <CContainer>
      <div class="header">
        <button class="back-btn" aria-label="Go back" @click="$router.go(-1)">
          <svg viewBox="0 0 24 24" class="icon-back">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
        <h2 class="app-title">Claim Rewards</h2>
      </div>

      <section class="points">
        <div class="points-left">
          <div class="label">Your Points</div>
         
            
            <span class="num">{{ companyName }}</span>
            <span class="unit"></span>
            <br />
            <div class="amount">
            <img class="coin" src="@/assets/img/new/coin.png" alt="coin" />
            <span class="num">{{ userPoints }}</span>
            <span class="unit">Points</span>
          </div>
        </div>
        <CButton class="history" @click="goHistory">
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M13 3a9 9 0 1 0 9 9h-2a7 7 0 1 1-7-7V3zm1 4h-2v6h6v-2h-4V7z"
            />
          </svg>
          History
        </CButton>
      </section>

      <h3 class="section-title">Claim Your Rewards</h3>
      <p v-if="errorMessage" class="error-banner">{{ errorMessage }}</p>

      <div v-if="isLoadingRewards" class="state loading-state">
        Loading rewards...
      </div>
      <div v-else-if="isEmptyState" class="state empty-state">
        <img
          src="@/assets/img/new/Illustration-point.png"
          alt="No rewards illustration"
        />
        <p>{{ emptyStateMessage }}</p>
      </div>
      
      <div v-else class="cards">
        <article
          v-for="reward in claimRewards"
          :key="reward.id"
          class="card"
        >
          <img class="bg" :src="reward.image" :alt="reward.title" />
          <div class="gradient"></div>

          <div class="top">
            <div class="title-pill">
              {{ reward.statusLabel || "Available Campaign" }}
            </div>
            <div class="points-badge">
              <img
                class="badge-coin"
                src="@/assets/img/new/coin.png"
                alt=""
              />
              <span>{{ reward.points }}</span>
            </div>
          </div>

          <div class="copy">
            <p class="headline">{{ reward.title }}</p>
            <p v-if="reward.requester" class="desc requester">
              Requested by <span>{{ reward.requester }}</span>
            </p>
            <p v-if="reward.createdAt" class="desc time">
              {{ formatDate(reward.createdAt) }}
            </p>
            <span :class="['status-pill', reward.statusKey]">
              {{ reward.statusLabel }}
            </span>
          </div>

          <CButton
            class="claim"
            :disabled="isClaiming || claimedRewardIds.includes(reward.id)"
            @click="onClaim(reward)"
          >
            <span v-if="claimedRewardIds.includes(reward.id)">Claimed</span>
            <span v-else-if="isClaiming && processingRewardId === reward.id"
              >Processing...</span
            >
            <span v-else>Claim</span>
          </CButton>
        </article>
      </div>

      <div v-if="showPopup" class="popup" @click.self="closePopup">
        <div class="sheet grabber">
          <img
            src="../assets/img/new/Illustration-point.png"
            class="sheet-img"
          />
          <h2 class="sheet-title">Insufficient Points</h2>
          <p class="sheet-text">
            You don't have enough points to claim this<br />reward. Earn more
            points to unlock it.
          </p>
          <h3 class="sheet-sub">Your Point : {{ userPoints }}</h3>
          <button class="sheet-btn" @click="closePopup">Ok</button>
        </div>
      </div>

      <div v-if="showSuccess" class="popup" @click.self="closeSuccess">
        <div class="sheet waiting grabber">
          <img
            src="../assets/img/waiting.png"
            class="sheet-img waiting-img"
            alt="waiting"
          />
          <h2 class="sheet-title waiting-title">Waiting for Approval</h2>
          <p class="sheet-text waiting-desc">
            Please hold on while the cashier reviews your request. This may take
            a moment.
          </p>
          <p class="waiting-hint">Please wait a moment...</p>
          <div class="sheet-actions">
            <button class="sheet-btn outline" @click="cancelClaim">
              Cancel Request
            </button>
            <button class="sheet-btn primary" @click="goHistory">
              Refresh Status
            </button>
          </div>
        </div>
      </div>
    </CContainer>
  </div>
</template>

<script>
const DEFAULT_REWARD_IMAGE = require("@/assets/img/new/claim1.png");

export default {
  name: "RewardsPage",
  data() {
    return {
      userPoints: 0,
      userInfo: null,
      companyId: null,
      isLoading: false,
      isLoadingRewards: false,
      hasFetchedRewards: false,
      showPopup: false,
      showSuccess: false,
      lastClaimedReward: null,
      processingRewardId: null,
      claimedRewardIds: [],
      isClaiming: false,
      errorMessage: "",
      claimRewards: [], 
      emptyStateMessage: "Belum ada reward yang tersedia saat ini.",
      companyName: "",
    };
  },
  computed: {
    hasRewards() {
      return Array.isArray(this.claimRewards) && this.claimRewards.length > 0;
    },
    isEmptyState() {
      return (
        this.hasFetchedRewards && !this.isLoadingRewards && !this.hasRewards
      );
    },
  },
  mounted() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.isLoading = true;
      this.isLoadingRewards = true;
      this.errorMessage = "";
      try {
        await this.fetchUser();
        this.hasFetchedRewards = true;
      } catch (error) {
        this.errorMessage = this.extractErrorMessage(
          error,
          "Unable to load rewards at the moment. Please try again."
        );
      } finally {
        this.isLoading = false;
        this.isLoadingRewards = false;
      }
    },
    async fetchUser() {
      let userId = null;
      try {
        const loginResponse = await this.$API.get(this.$LoginUserDetail, {
          withCredentials: true,
        });

        if (loginResponse.data) {
          this.userInfo = loginResponse.data;
          this.companyId =
            loginResponse.data?.companyId ??
            loginResponse.data?.CompanyId ??
            null;
          userId = loginResponse.data.id;
        }
      } catch (error) {
        console.warn("Failed to fetch $LoginUserDetail.");
      }

      if (!userId) {
        throw new Error("User ID is not available to fetch point summary.");
      }

      try {
        const summaryResponse = await this.$API.get(
          this.$UserSummaryPointCompany + "?userId=" + userId + "&companyId=" + this.$route.query.companyId,
          { withCredentials: true }
        );

        if (summaryResponse.status === 200 && summaryResponse.data) {
          const summary = Array.isArray(summaryResponse.data)
            ? summaryResponse.data[0]
            : summaryResponse.data;

          if (summary) {
            this.userPoints = Number(
              summary.TotalPoint ?? summary.Total_Point ?? 0
            );

            this.companyName = summary.CompanyName || "";
            
            const rawRewards = summary.AvailableRewards ?? summary.Campaigns ?? summary.rewards ?? [];
            this.claimRewards = Array.isArray(rawRewards) 
                ? rawRewards.map((item) => this.normalizeReward(item))
                : [];
          } else {
            this.userPoints = 0;
            this.claimRewards = [];
          }
        }
      } catch (error) {
        console.error("Failed to fetch UserSummaryPoint and rewards", error);
        throw error;
      }
    },
    
    normalizeReward(raw) {
      const imagePath = raw?.FilePath || raw?.ImagePath || raw?.image || "";
      return {
        id: raw?.Id ?? raw?.id ?? raw?.CampaignId ?? `reward-${Date.now()}-${Math.random()}`,
        title: raw?.Title || raw?.title || raw?.CampaignTitle || "Reward Campaign",
        points: Number(raw?.PointsNeeded ?? raw?.pointsNeeded ?? raw?.Points ?? 0) || 0,
        image:
          typeof imagePath === "string" && imagePath.trim().length > 0
            ? imagePath
            : DEFAULT_REWARD_IMAGE,
        statusKey: "available",
        statusLabel: "Available",
        raw,
      };
    },
    async onClaim(item) {
      if (!item || typeof item !== "object") return;
      
      // Mengambil Campaign ID dari raw data reward
      const campaignId = item.raw.Id ?? item.raw.CampaignId; 
      const userId = this.userInfo?.id; // Menggunakan userId yang didapat dari $LoginUserDetail

      if (!userId || !campaignId) {
        this.errorMessage = "Informasi pengguna atau Campaign ID tidak ditemukan.";
        return;
      }

      if (this.isClaiming || this.claimedRewardIds.includes(item.id)) return;
      this.errorMessage = "";
      const cost = Number(item.points) || 0;

      if (this.userPoints < cost) {
        this.showPopup = true;
        return;
      }

      this.isClaiming = true;
      this.processingRewardId = item.id;

      try {
        const formData = new FormData();
        formData.append('userId', userId);
        formData.append('campaignId', campaignId);
        
        const response = await this.$API.post(
          this.$InsertClaimPoints,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" }, // Headers sudah benar, tapi Axios/Vue Resource mungkin menambahkannya otomatis saat menggunakan FormData
            withCredentials: true,
          }
        );

        if (response.status === 200 || response.data?.Code === 200) {
          this.userPoints -= cost;
          this.claimedRewardIds.push(item.id);
          this.lastClaimedReward = { id: item.id, points: cost };
          this.showSuccess = true;
        } else {
          this.errorMessage =
            response.data?.message || "Klaim gagal. Silakan coba lagi.";
        }
      } catch (error) {
        this.errorMessage = this.extractErrorMessage(
          error,
          "Terjadi kesalahan saat mengklaim. Silakan coba sebentar lagi."
        );
      } finally {
        this.isClaiming = false;
        this.processingRewardId = null;
      }
    },
    closePopup() {
      this.showPopup = false;
    },
    closeSuccess() {
      this.showSuccess = false;
      this.lastClaimedReward = null;
    },
    cancelClaim() {
      if (this.lastClaimedReward) {
        // Rollback poin dan status claimed jika request dibatalkan
        this.claimedRewardIds = this.claimedRewardIds.filter(
          (id) => id !== this.lastClaimedReward.id
        );
        this.userPoints += this.lastClaimedReward.points;
      }
      this.closeSuccess();
    },
    formatStatusLabel(status) {
      if (!status) return "";
      const text = status.toString().trim();
      if (!text) return "";
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    },
    formatDate(date) {
      if (!date) return "";
      try {
        if (this.$moment) {
          const m = this.$moment(date);
          if (m.isValid()) return m.format("DD MMM YYYY, HH:mm");
        }
        const d = new Date(date);
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      } catch (error) {}
      return date;
    },
    extractErrorMessage(error, fallback) {
      if (error?.response?.data?.message) return error.response.data.message;
      if (error?.message) return error.message;
      return fallback;
    },
    goHistory() {
      this.$router.push({ name: "HistoryPoint" }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.header {
  position: relative;
  height: 75px;
  display: block;
  padding: 0;
}

.back-btn {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  background: transparent;
  border: 0;
  color: #111827;
  cursor: pointer;
}
.icon-back {
  width: 22px;
  height: 22px;
  stroke: currentColor;
  fill: none;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.app-title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.points {
  margin-top: 8px;
}

.rewards-page {
  background: #fff;
  min-height: 100vh;
  padding-bottom: 24px;
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0 6px;
}
.header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}
.back {
  background: transparent;
  border: none;
  font-size: 20px;
  padding: 6px 8px;
}
.ghost {
  width: 28px;
}
.points {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #111827;
  color: #fff;
  border-radius: 16px;
  padding: 16px;
  margin-top: 8px;
}
.points-left .label {
  font-size: 12px;
  opacity: 0.9;
}
.points-left .amount {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.points-left .coin {
  width: 22px;
  height: 22px;
}
.points-left .num {
  font-size: 18px;
  font-weight: 700;
}
.points-left .unit {
  font-size: 14px;
  font-weight: 600;
  margin-left: 4px;
  opacity: 0.9;
}
.history {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
}
.section-title {
  margin: 18px 0 10px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.company-section {
  margin-bottom: 24px;
}

.company-title {
  margin: 18px 0 10px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 4px;
}
.error-banner {
  margin: 6px 0 14px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #fef3f2;
  color: #b42318;
  font-size: 12px;
  font-weight: 600;
}
.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 36px 16px;
  text-align: center;
  color: #6c737f;
  font-size: 14px;
}
.loading-state {
  font-weight: 600;
}
.empty-state img {
  width: 160px;
  margin-bottom: 16px;
}

.cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.card {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  min-height: 176px;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.12);
}
.bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
}
.gradient {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.25) 0%,
    rgba(0, 0, 0, 0.6) 55%,
    rgba(0, 0, 0, 0.75) 100%
  );
}

.top {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px;
}
.title-pill {
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.2px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  max-width: 70%;
  margin: 0;
}
.points-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.65);
  color: #fff;
  font-weight: 700;
  font-size: 12px;
}
.badge-coin {
  width: 18px;
  height: 18px;
}

.copy {
  position: relative;
  z-index: 1;
  color: #fff;
  padding: 2px 12px 56px;
}
.copy .headline {
  font-size: 32px;
  line-height: 1.05;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.2px;
}
.copy .desc {
  font-size: 12px;
  margin-top: 6px;
  color: #e5e7eb;
}
.copy .desc.requester span {
  font-weight: 600;
}
.copy .desc.time {
  opacity: 0.75;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: capitalize;
  margin-top: 12px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}
.status-pill.pending,
.status-pill.collecting {
  background: rgba(255, 255, 255, 0.22);
  color: #fde68a;
}
.status-pill.ready,
.status-pill.claimed {
  background: rgba(34, 197, 94, 0.25);
  color: #bbf7d0;
}
.status-pill.failed {
  background: rgba(239, 68, 68, 0.28);
  color: #fecaca;
}

.claim {
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 1;
  background: #fff;
  color: #7e73fd;
  border: 1px solid #fff;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 800;
  box-shadow: 0 4px 10px rgba(16, 24, 40, 0.18);
}
.claim:disabled {
  background: #f3f4f6;
  border-color: #f3f4f6;
  color: #9ca3af;
  box-shadow: none;
  cursor: not-allowed;
}
.claim:not(:disabled):active {
  transform: translateY(1px);
}

.popup {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding: 12px 16px;
  z-index: 50;
  transform: translateX(0%);
}
.sheet {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 18px;
  text-align: center;
  position: relative;
  margin: 0 auto;
}
.sheet-img {
  max-width: 100%;
}
.sheet-title {
  color: #171717;
  font-size: 18px;
  font-weight: 600;
  margin-top: 8px;
}
.sheet-text {
  color: #6c737f;
  font-size: 14px;
  margin-top: 6px;
}
.sheet-sub {
  font-size: 14px;
  margin-top: 6px;
  font-style: italic;
  color: #928afd;
  font-weight: 600;
}
.sheet-btn {
  width: 100%;
  margin-top: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 14px;
  background: #fff;
  font-weight: 600;
  cursor: pointer;
}
.grabber::before {
  content: "";
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 56px;
  height: 5px;
  border-radius: 999px;
  background: #101828;
}
.sheet.waiting {
  padding: 28px 18px 32px;
}
.waiting-img {
  max-width: 220px;
  margin: 8px auto 16px;
}
.waiting-title {
  color: #171717;
  font-size: 20px;
  font-weight: 600;
}
.waiting-desc {
  max-width: 280px;
  margin: 12px auto 8px;
}
.waiting-hint {
  color: #928afd;
  font-style: italic;
  font-size: 14px;
}
.sheet-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}
.sheet-actions .sheet-btn {
  flex: 1;
  margin-top: 0;
}
.sheet-btn.primary {
  background: #7e73fd;
  border-color: #7e73fd;
  color: #fff;
}
.sheet-btn.outline {
  background: #fff;
  color: #4b5563;
  border-color: #d0d5dd;
}
</style>